/**
 * WorkspaceLayout: the frame for the tool pages (Search, Scrape, Parse, Map, Crawl). Form on the left, results on
 * the right, each pane with exactly one scroll area, sized by flexbox instead of calc(100vh - N).
 *
 *   Page.getLayout = page => <Layout>{page}</Layout>;     // DashboardLayout, unchanged
 *
 *   <WorkspaceLayout
 *     title="Scrape"
 *     onShowCode={() => setCodeOpen(true)}               "Code" button; an icon button on phones, never hidden
 *     actions={<Button variant="secondary" size="sm">Save as preset</Button>}
 *     form={<>...fields...</>}
 *     formFooter={<Button type="submit" form="scrape-form" fullWidth loading={running}>Scrape</Button>}
 *     formProps={{ id: "scrape-form", onSubmit: handleSubmit }}   renders the left pane as a <form>
 *     results={<OutputSection ... />}
 *     resultsScroll={false}                               results manage their own scroll (tabs + viewer)
 *   />
 *
 * Layout rules
 * - lg (1024px) and up: two columns side by side filling the height under the header. The left pane scrolls its
 *   fields (with a fade at the bottom while more is hidden) above a pinned footer; the right pane scrolls its
 *   results. The page itself does not scroll.
 * - Below lg: one column, results after the form, panes at natural height, and only the page scrolls.
 * - split "even" (default, 1:1) | "form-narrow" (2:3) | "form-wide" (3:2)
 * - header={<PageHeader .../>} replaces the built-in header when a page needs something custom.
 * - minPaneHeight keeps panes usable on short screens at lg+ (default 480px); below that the page scrolls.
 * Panes use the Card surface. Pass className / formClassName / resultsClassName to adjust padding.
 */
import { FormHTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import PageHeader from "./PageHeader";
import Button from "@/components/ui/Button";
import IconButton from "@/components/ui/IconButton";

interface WorkspaceLayoutProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  onShowCode?: () => void;
  codeLabel?: string;
  header?: ReactNode;
  form: ReactNode;
  formFooter?: ReactNode;
  formProps?: FormHTMLAttributes<HTMLFormElement>;
  results: ReactNode;
  resultsHeader?: ReactNode;
  resultsScroll?: boolean;
  split?: "even" | "form-narrow" | "form-wide";
  minPaneHeight?: number;
  className?: string;
  formClassName?: string;
  resultsClassName?: string;
  formLabel?: string;
  resultsLabel?: string;
}

const SPLIT = {
  even: "lg:grid-cols-2",
  "form-narrow": "lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]",
  "form-wide": "lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]",
};

/** Scroll area that shows a soft fade at the bottom while there is more content below. */
export function FadeScroll({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [check]);
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={ref} onScroll={check} className={`min-h-0 flex-1 lg:overflow-y-auto lg:overscroll-contain ${className}`}>
        <div>{children}</div>
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 bottom-0 hidden h-10 bg-gradient-to-t from-surface to-transparent transition-opacity duration-150 lg:block ${
          more ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}

export default function WorkspaceLayout({
  title,
  description,
  actions,
  onShowCode,
  codeLabel = "Code",
  header,
  form,
  formFooter,
  formProps,
  results,
  resultsHeader,
  resultsScroll = true,
  split = "even",
  minPaneHeight = 480,
  className = "",
  formClassName = "",
  resultsClassName = "",
  formLabel,
  resultsLabel = "Results",
}: WorkspaceLayoutProps) {
  const headerActions =
    actions || onShowCode ? (
      <>
        {actions}
        {onShowCode && (
          <>
            {/* Wrappers carry the breakpoint display, so it can't fight the button's own inline-flex. */}
            <span className="hidden sm:inline-flex">
              <Button variant="secondary" size="sm" leftIcon="mdi:code-tags" onClick={onShowCode}>
                {codeLabel}
              </Button>
            </span>
            <span className="inline-flex sm:hidden">
              <IconButton icon="mdi:code-tags" label={`Show ${codeLabel.toLowerCase()}`} variant="secondary" onClick={onShowCode} />
            </span>
          </>
        )}
      </>
    ) : null;

  const pane = "flex min-w-0 flex-col rounded-card border border-line bg-surface shadow-card";
  const formInner = (
    <>
      <FadeScroll className={`p-5 sm:p-6 ${formClassName}`}>{form}</FadeScroll>
      {formFooter && <div className="shrink-0 border-t border-line px-5 py-4 sm:px-6">{formFooter}</div>}
    </>
  );

  return (
    <div className={`flex flex-col gap-5 lg:h-full lg:min-h-0 ${className}`}>
      {header ??
        (title !== undefined && (
          <PageHeader title={title} description={description} spacing="none" className="shrink-0">
            {headerActions}
          </PageHeader>
        ))}

      <div
        className={`grid grid-cols-1 gap-5 lg:min-h-0 lg:flex-1 lg:grid-rows-[minmax(0,1fr)] ${SPLIT[split]}`}
        style={{ ["--ws-min" as string]: `${minPaneHeight}px` }}
      >
        {formProps ? (
          <form {...formProps} aria-label={formLabel} className={`${pane} lg:min-h-[var(--ws-min)] ${formProps.className ?? ""}`}>
            {formInner}
          </form>
        ) : (
          <div aria-label={formLabel} role={formLabel ? "group" : undefined} className={`${pane} lg:min-h-[var(--ws-min)]`}>
            {formInner}
          </div>
        )}

        <section aria-label={resultsLabel} className={`${pane} lg:min-h-[var(--ws-min)]`}>
          {resultsHeader && <div className="shrink-0 border-b border-line px-5 py-3">{resultsHeader}</div>}
          <div
            className={`flex min-h-0 flex-1 flex-col ${resultsScroll ? "lg:overflow-y-auto lg:overscroll-contain" : "lg:overflow-hidden"} ${resultsClassName}`}
          >
            {results}
          </div>
        </section>
      </div>
    </div>
  );
}
